<template>
  <div class="language-switcher">
    <button 
      @click="toggleLanguage" 
      class="language-btn modern-btn"
      :class="{ 'light-mode': isLightMode, 'mobile-mode': isMobileMode }"
      :title="$t('nav.language')"
    >
      <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
        <path d="M17.9,17.39C17.64,16.59 16.89,16 16,16H15V13A1,1 0 0,0 14,12H8V10H10A1,1 0 0,0 11,9V7H13A2,2 0 0,0 15,5V4.59C17.93,5.77 20,8.64 20,12C20,14.08 19.2,15.97 17.9,17.39M11,19.93C7.05,19.44 4,16.08 4,12C4,11.38 4.08,10.78 4.21,10.21L9,15V16A2,2 0 0,0 11,18M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"/>
      </svg>
      <span class="language-code">{{ currentLanguage.toUpperCase() }}</span>
    </button>
  </div>
</template>

<script>
export default {
  name: 'LanguageSwitcher',
  props: {
    variant: {
      type: String,
      default: 'dark', // 'dark', 'light', or 'mobile'
      validator: (value) => ['dark', 'light', 'mobile'].includes(value)
    }
  },
  computed: {
    currentLanguage() {
      return this.$i18n.locale
    },
    isLightMode() {
      return this.variant === 'light'
    },
    isMobileMode() {
      return this.variant === 'mobile'
    }
  },
  methods: {
    toggleLanguage() {
      const newLanguage = this.currentLanguage === 'en' ? 'de' : 'en'
      this.$i18n.locale = newLanguage
      localStorage.setItem('language', newLanguage)
    }
  }
}
</script>

<style scoped>
.language-switcher {
  display: flex;
  align-items: center;
}

.language-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border: 1px solid;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  font-family: inherit;
  min-height: 44px;
  position: relative;
  overflow: hidden;
  
  /* Default styling for dark backgrounds */
  background: rgba(255, 255, 255, 0.07);
  border-color: rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.75);
}

.language-btn:hover {
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

/* Light mode styling — kept same as dark since header is now dark */
.language-btn.light-mode {
  background: rgba(255, 255, 255, 0.07);
  border-color: rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.75);
}

.language-btn.light-mode:hover {
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.15);
  color: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

/* Mobile mode styling for sidebar */
.language-btn.mobile-mode {
  background: rgba(255, 255, 255, 0.07);
  border-color: rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.75);
  padding: 0.5rem 0.75rem;
  font-size: 0.8rem;
  min-width: 60px;
  min-height: 36px;
}

.language-btn.mobile-mode:hover {
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.15);
  color: #fff;
  transform: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.language-btn svg {
  flex-shrink: 0;
}

.language-code {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', monospace;
  font-weight: 700;
  white-space: nowrap;
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .language-btn.light-mode {
    background: rgba(255, 255, 255, 0.07);
    border-color: rgba(255, 255, 255, 0.15);
    color: rgba(255, 255, 255, 0.75);
  }

  .language-btn.light-mode:hover {
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(255, 255, 255, 0.15);
    color: #fff;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }

  .language-btn.mobile-mode {
    background: rgba(255, 255, 255, 0.07);
    border-color: rgba(255, 255, 255, 0.15);
    color: rgba(255, 255, 255, 0.75);
  }

  .language-btn.mobile-mode:hover {
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(255, 255, 255, 0.15);
    color: #fff;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }
}

@media (max-width: 480px) {
  .language-btn {
    padding: 0.5rem 0.75rem;
    font-size: 0.8rem;
    min-height: 40px;
  }
  
  .language-btn svg {
    width: 16px;
    height: 16px;
  }
}
</style> 